import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Typography,
} from "@mui/material";
import { toast } from "react-toastify";

import { completeBedCleaning, getBedsAwaitingCleaning } from "../../api/ipdEnhancementsApi";

// Collapsible list of beds currently in "cleaning", with a "Mark Cleaned"
// action that returns each bed to "available".
export default function HousekeepingSection({ onDone }) {
  const [expanded, setExpanded] = useState(false);
  const [beds, setBeds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [completing, setCompleting] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const list = await getBedsAwaitingCleaning();
      setBeds(Array.isArray(list) ? list : []);
    } catch (err) {
      console.error("Failed to load cleaning beds:", err);
      toast.error("Failed to load beds awaiting cleaning.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (expanded) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expanded]);

  const handleComplete = async (bedCode) => {
    setCompleting(bedCode);
    try {
      const data = await completeBedCleaning(bedCode);
      if (data?.status === "Success") {
        toast.success(data.message || `Bed ${bedCode} is now available`);
        setBeds((prev) => prev.filter((b) => b.code !== bedCode));
        // Refresh the parent bed census — completing a clean here otherwise only
        // updated this local list, leaving the census showing a stale status.
        onDone?.();
      } else {
        toast.error(data?.message || "Could not complete cleaning.");
      }
    } catch (err) {
      console.error("Complete cleaning failed:", err);
      toast.error(
        err?.response?.data?.message || "Could not complete cleaning."
      );
    } finally {
      setCompleting(null);
    }
  };

  return (
    <Box sx={{ mb: 3 }}>
      <Box
        onClick={() => setExpanded((v) => !v)}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          cursor: "pointer",
          backgroundColor: "#fff4e5",
          border: "1px solid #ffcc80",
          borderRadius: 1.5,
          px: 2,
          py: 1,
        }}
      >
        <Typography sx={{ fontWeight: 700, color: "#9a5b00" }}>
          Beds Awaiting Cleaning
          {beds.length > 0 && (
            <Chip
              size="small"
              label={beds.length}
              sx={{ ml: 1, backgroundColor: "#ff9800", color: "#fff" }}
            />
          )}
        </Typography>
        <Typography sx={{ color: "#9a5b00", fontSize: "13px" }}>
          {expanded ? "Hide ▲" : "Show ▼"}
        </Typography>
      </Box>
      <Collapse in={expanded}>
        <Box sx={{ mt: 1 }}>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
              <CircularProgress size={20} />
            </Box>
          ) : beds.length === 0 ? (
            <Typography sx={{ color: "#777", fontSize: "13px", px: 1, py: 1 }}>
              No beds awaiting cleaning.
            </Typography>
          ) : (
            beds.map((bed) => (
              <Box
                key={bed.code}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  border: "1px solid #eee",
                  borderRadius: 1,
                  px: 1.5,
                  py: 0.8,
                  mb: 0.8,
                }}
              >
                <Typography sx={{ fontSize: "13px" }}>
                  <strong>{bed.code}</strong>
                  {bed.location ? ` — ${bed.location}` : ""}
                  {/* An occupied bed can now be flagged for cleaning without
                      losing its occupancy (Epic 5) — call that out, since it
                      means cleaning around a patient, not a turnover clean. */}
                  {String(bed.status || "").toLowerCase() === "occupied" && (
                    <Chip
                      size="small"
                      variant="outlined"
                      label="Occupied"
                      sx={{ ml: 1, height: 20, fontSize: 11 }}
                    />
                  )}
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  disabled={completing === bed.code}
                  onClick={() => handleComplete(bed.code)}
                >
                  {completing === bed.code ? "Saving..." : "Mark Cleaned"}
                </Button>
              </Box>
            ))
          )}
        </Box>
      </Collapse>
    </Box>
  );
}
